import React, { useState } from "react";
import {
  TextField,
  Button,
  Container,
  Paper,
  Box,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import logo from "../assets/cestasoft-logo.png";
import "./login.css";

const Login = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    if (localStorage.getItem("isAuthenticated") === "true") {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    setError("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

const handleSubmit = async (event) => {
  event.preventDefault();

  setError("");

  const errors = {};

  if (!formData.username.trim()) {
    errors.username = "Username is required";
  }

  if (!formData.password) {
    errors.password = "Password is required";
  }

  if (Object.keys(errors).length > 0) {
    setFieldErrors(errors);
    return;
  }

  try {
    setIsLoading(true);

    // Temporary hardcoded login
    if (
      formData.username === "admin" &&
      formData.password === "admin"
    ) {
      // Temporary login flag
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("username", "admin");

      // Navigate to dashboard
      navigate("/dashboard");
    } else {
      setError("Invalid username or password.");
    }
  } catch (err) {
    setError("Login failed. Please try again.");
  } finally {
    setIsLoading(false);
  }
};

 

  return (
    <Container maxWidth={false} className="login-container">
      <Paper elevation={8} className="login-card">
        <img src={logo} alt="Cestasoft Logo" className="login-logo" />

        <div className="form-container">
          <h1 className="login-title">Login</h1>

          {error && (
            <Alert
              severity="error"
              className="error-alert"
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            autoComplete="off"
          >
            <TextField
              fullWidth
              label="Username"
              name="username"
              variant="outlined"
              value={formData.username}
              onChange={handleInputChange("username")}
              disabled={isLoading}
              error={Boolean(fieldErrors.username)}
              helperText={fieldErrors.username}
              className="styled-text-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f8f9fa",
                  margin: "0 0 16px",
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #f8f9fa inset",
                    WebkitTextFillColor: "#000",
                    caretColor: "#000",
                    borderRadius: "inherit",
                  },
                },
              }}
              autoComplete="username"
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              value={formData.password}
              onChange={handleInputChange("password")}
              disabled={isLoading}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password}
              className="styled-text-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      disabled={isLoading}
                      type="button"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f8f9fa",
                  margin: "0 0 16px",
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #f8f9fa inset",
                    WebkitTextFillColor: "#000",
                    caretColor: "#000",
                    borderRadius: "inherit",
                  },
                },
              }}
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              className="styled-button"
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LoginIcon />
                )
              }
              sx={{
                marginTop: "16px",
                backgroundColor: "#F84122",
                color: "#ffffff",
                borderRadius: "12px",
                padding: "14px",
                fontWeight: "bold",
                fontSize: "16px",
                textTransform: "none",
                width: "100%",
              }}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </Button>

          
          </Box>
        </div>
      </Paper>
    </Container>
  );
};

export default Login;